import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-white shadow-xs hover:bg-primary/90    transition-all duration-300 ease-in-out",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20   transition-all duration-300 ease-in-out",
        outline:
          "border bg-white hover:bg-slate-100 hover:text-primary   transition-all duration-300 ease-in-out",
        secondary:
          "border bg-blue-600 text-white shadow-xs hover:bg-blue-600/80   transition-all duration-300 ease-in-out",
        ghost:
          "border border-transparent hover:bg-slate-100 hover:text-slate-900   transition-all duration-300 ease-in-out",
        link: "text-primary underline-offset-4 hover:underline   transition-all duration-300 ease-in-out",
      },
      size: {
        default: "h-10 rounded-md px-6 has-[>svg]:px-4 text-sm font-semibold",
        sm: "h-8 rounded-md gap-1.5 px-4 has-[>svg]:px-2.5 text-xs font-semibold",
        xl: "h-12 rounded-md px-6 has-[>svg]:px-4 text-base font-semibold",
        "2xl":
          "h-12 lg:h-14 rounded-md px-4 lg:px-8 has-[>svg]:px-8 text-base lg:text-xl font-semibold",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  if (loading) {
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        disabled
        {...props}
      >
        <div className="flex items-center justify-center">
          <div
            className={cn(
              "border-whiet h-6 w-6 animate-spin rounded-full border-t-2 border-b-2",
              className
            )}
          ></div>
        </div>
      </Comp>
    );
  }

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
